import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance from "../api";

interface SingleQueryState {
  query: { name: string };
}
interface RootState {
  queryBuilder: {
    queries: SingleQueryState[];
    currentTabIndex: number;
  };
  meta: any;
  queryMeta: {
    metaStates: MetaState[];
    meta: Meta[] | null;
    cubesMap: Map<string, Cube[]>;
  };
}

export const getCubes = createAsyncThunk(
  "olapmeta",
  async (currentTabIndex: number) => {
    const response = await axiosInstance.get(`/rest/olapmeta`);
    return response.data;
  },
);

interface MetadataParams {
  connection: string;
  catalog: string;
  schema: string;
  cube: string;
  currentTabIndex: number;
}

export const getMetadata = createAsyncThunk(
  "olapmeta/metadata",
  async (
    { connection, catalog, schema, cube, currentTabIndex }: MetadataParams,
    { dispatch },
  ) => {
    const response = await axiosInstance.get(
      `/rest/olapmeta/${connection}/${catalog}/${schema}/${cube}/metadata?nocache=${Math.random()}`,
    );
    dispatch(
      getMetadataGroup({ connection, catalog, schema, cube, currentTabIndex }),
    );
    return response.data;
  },
);

export const getMetadataGroup = createAsyncThunk(
  "olapmeta/metadataGroup",
  async ({
    connection,
    catalog,
    schema,
    cube,
    currentTabIndex,
  }: MetadataParams) => {
    const response = await axiosInstance.get(
      `/rest/olapmeta/${connection}/${catalog}/${schema}/${cube}/metadata/group?nocache=${Math.random()}`,
    );
    return response.data;
  },
);

export const fetchMembersMembers = createAsyncThunk(
  "query/members",
  async (uniquename: string, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const queryName =
      state.queryBuilder.queries[state.queryBuilder.currentTabIndex].query.name;
    const response = await axiosInstance.get(
      `/rest/olapquery/${queryName}/members/${uniquename}`,
    );
    return response.data;
  },
);

export const getNamedSetList = createAsyncThunk(
  "query/namedSet",
  async (uniquename: string, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const queryName =
      state.queryBuilder.queries[state.queryBuilder.currentTabIndex].query.name;
    const schema =
      state.meta.metaStates[state.queryBuilder.currentTabIndex].selectedCube
        ?.schema;
    const response = await axiosInstance.get(
      `/rest/namedset/${schema}/${uniquename}`,
    );
    return response.data;
  },
);

export const createNameSet = createAsyncThunk(
  "query/createNamedSet",
  async (payload: URLSearchParams) => {
    const namedSetName = payload.get("namedSetName") ?? "";
    const response = await axiosInstance.post(
      `/rest/namedset/new/${namedSetName}`,
      payload,
    );
    return response.data;
  },
);

export const updateNameSet = createAsyncThunk(
  "query/updateNamedSet",
  async (payload: URLSearchParams) => {
    const namedSetId = payload.get("namedSetId") ?? "";
    const namedSetName = payload.get("namedSetName") ?? "";
    const ismemberupdated = payload.get("ismemberupdated") ?? "N";

    const response = await axiosInstance.post(
      `/rest/namedset/update/${namedSetId}/${namedSetName}/${ismemberupdated}`,
      payload,
    );
    return response.data;
  },
);

export const deleteNameSet = createAsyncThunk(
  "query/deleteNamedSet",
  async (payload: { nsids: string[]; levelName: string }, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const schema =
      state.meta.metaStates[state.queryBuilder.currentTabIndex].selectedCube
        ?.schema ?? "";

    const params = new URLSearchParams();
    params.append("schemaName", schema);
    params.append("levelName", payload.levelName);
    payload.nsids.forEach((id) => params.append("nsid[]", id));

    const response = await axiosInstance.delete(`/rest/namedset/delete/`, {
      data: params,
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });
    return response.data;
  },
);

export const fetchMembersOfPage = createAsyncThunk(
  "query/members",
  async (payload: MemberPageRequest, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const queryName =
      state.queryBuilder.queries[state.queryBuilder.currentTabIndex].query.name;
    if (payload.searchText) {
      const response = await axiosInstance.get(
        `/rest/olapquery/${queryName}/members/${payload.uniquename}/page/${payload.start}/${payload.end}?search=${encodeURIComponent(payload.searchText)}`,
      );
      return response.data;
    } else {
      const response = await axiosInstance.get(
        `/rest/olapquery/${queryName}/members/${payload.uniquename}/page/${payload.start}/${payload.end}`,
      );
      return response.data;
    }
  },
);

export const fetchMembersCount = createAsyncThunk(
  "query/members/count",
  async (payload: MemberPageRequest, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const queryName =
      state.queryBuilder.queries[state.queryBuilder.currentTabIndex].query.name;
    const response = await axiosInstance.get(
      `/rest/olapquery/${queryName}/members/count/${payload.uniquename}?search=${payload.searchText}`,
    );
    return response.data;
  },
);

interface Annotation {
  viewname: string;
  categoryGroup: string;
  "cube.category": string;
  "data.load.type": string;
  "load.date": string;
}

interface MemberPageRequest {
  uniquename: string;
  start?: number;
  end?: number;
  searchText?: string;
}

export interface Cube {
  name: string;
  uniqueName: string;
  schema: string;
  caption: string;
  annotations: Annotation;
  levelsCounts: Map<string, number>;
}

interface Schema {
  name: string;
  uniqueName: string;
  cubes: Cube[];
}

interface Catalog {
  name: string;
  uniqueName: string;
  schemas: Schema[];
}

interface Meta {
  catalogs: Catalog[];
  altCaption: string;
  name: string;
  uniqueName: string;
}

interface Measures {
  altCaption: string;
  caption: string;
  uniqueName: string;
  description: string;
  dimensionName: string;
  levelUniqueName: string;
  hierarchyUniqueName: string;
  dimensionUniqueName: string;
  name: string;
  properties: {};
}

interface DimensionsType {
  altCaption: string;
  uniqueName: string;
  name: string;
  properties: {};
  annotations: {};
  caption: string;
  description: string;
  visible: boolean;
  hierarchies: string;
}

interface MetadataGroupType {
  name: string;
  levels: string[];
}
interface MetaState {
  selectedCube: Cube | null;
  // cubesMap: Map<string, Cube[]>;
  measures: Measures[] | null;
  results: null;
  dimensions: DimensionsType[] | null;
  metadataGroup: MetadataGroupType[] | null;
  loading: boolean;
  error: string | null;
  properties?: any;
}

const singleMetaState: MetaState = {
  selectedCube: null,
  // cubesMap: new Map<string, Cube[]>(),
  measures: null,
  results: null,
  dimensions: null,
  metadataGroup: null,
  error: null,
  loading: false,
};

const initialState: {
  metaStates: MetaState[];
  meta: Meta[] | null;
  cubesMap: Map<string, Cube[]>;
} = {
  meta: null,
  cubesMap: new Map<string, Cube[]>(),
  metaStates: [{ ...singleMetaState }],
};

const queryMetaSlice = createSlice({
  name: "queryMeta",
  initialState,
  reducers: {
    setSelectedCube(
      state,
      action: PayloadAction<{ selectedCube: Cube; currentActiveTab: number }>,
    ) {
      state.metaStates[action.payload.currentActiveTab].selectedCube =
        action.payload.selectedCube;
    },
    setCubesMap(
      state,
      action: PayloadAction<{ cubesMap: Map<string, Cube[]> }>,
    ) {
      state.cubesMap = { ...action.payload.cubesMap };
    },
    removeMetaState(state, action) {
      state.metaStates.splice(action.payload, 1);
    },
    resetMetaState(state) {
      state.metaStates = [{ ...singleMetaState }];
    },
    addNewMetaState(state) {
      state.metaStates.push({
        ...singleMetaState,
      });
    },
    cloneMetaState(state, action) {
      state.metaStates.push({
        ...state.metaStates[action.payload],
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCubes.pending, (state, action) => {
        const currentTabIndex = action.meta.arg;

        state.metaStates[currentTabIndex].loading = true;
      })
      .addCase(getCubes.fulfilled, (state, action) => {
        const currentTabIndex = action.meta.arg;

        state.metaStates[currentTabIndex].loading = false;
        state.meta = action.payload;
        state.metaStates[currentTabIndex].error = null;
        let selectedCube = null;

        if (
          action?.payload &&
          action.payload.length > 0 &&
          action.payload[0].catalogs &&
          action.payload[0].catalogs.length > 0 &&
          action.payload[0].catalogs[0].schemas &&
          action.payload[0].catalogs[0].schemas.length > 0 &&
          action.payload[0].catalogs[0].schemas[0].cubes &&
          action.payload[0].catalogs[0].schemas[0].cubes.length > 0
        ) {
          selectedCube = action.payload[0].catalogs[0].schemas[0].cubes[0];
        }

        // state.metaStates[currentTabIndex].selectedCube = selectedCube;
      })
      .addCase(getCubes.rejected, (state, action) => {
        const currentTabIndex = action.meta.arg;

        state.metaStates[currentTabIndex].loading = false;
        state.metaStates[currentTabIndex].error =
          "Error while fetching cubes: " +
          (action.error.message || "Unknown error");
      })
      .addCase(getMetadata.pending, (state, action) => {
        const { currentTabIndex } = action.meta.arg;
        state.metaStates[currentTabIndex].loading = true;
      })
      .addCase(getMetadata.fulfilled, (state, { meta, payload }) => {
        const { currentTabIndex } = meta.arg;
        state.metaStates[currentTabIndex].loading = false;
        state.metaStates[currentTabIndex].measures = payload.measures;
        state.metaStates[currentTabIndex].dimensions = payload.dimensions;
        state.metaStates[currentTabIndex].properties = payload.properties;
        state.metaStates[currentTabIndex].error = null;
      })
      .addCase(getMetadata.rejected, (state, action) => {
        const { currentTabIndex } = action.meta.arg;

        state.metaStates[currentTabIndex].loading = false;
        state.metaStates[currentTabIndex].error =
          "Error while fetching metadata: " +
          (action.error.message || "Unknown error");
      })
      .addCase(getMetadataGroup.pending, (state, action) => {
        const { currentTabIndex } = action.meta.arg;

        state.metaStates[currentTabIndex].loading = true;
      })
      .addCase(getMetadataGroup.fulfilled, (state, { meta, payload }) => {
        const { currentTabIndex } = meta.arg;

        state.metaStates[currentTabIndex].loading = false;
        state.metaStates[currentTabIndex].metadataGroup = payload;
        state.metaStates[currentTabIndex].error = null;
      })
      .addCase(getMetadataGroup.rejected, (state, action) => {
        const { currentTabIndex } = action.meta.arg;

        state.metaStates[currentTabIndex].loading = false;
        state.metaStates[currentTabIndex].error =
          "Error while fetching metadata group: " +
          (action.error.message || "Unknown error");
      });
  },
});

export const {
  setSelectedCube,
  setCubesMap,
  addNewMetaState,
  cloneMetaState,
  removeMetaState,
  resetMetaState,
} = queryMetaSlice.actions;
export default queryMetaSlice.reducer;
